import { z } from 'zod';
const UtilizationMetricSchema = z.enum([
    'actualUtilization',
    'committedUtilization',
    'bonusCredits',
]);
const UtilizationDimensionSchema = z.enum(['flow', 'block', 'month']);
const UtilizationCursorSchema = z.string().min(1).max(2_048);
const UtilizationQueryBaseSchema = z.strictObject({
    organizationId: z.uuid().describe('One organization the caller is authorized to access.'),
    limit: z.coerce
        .number()
        .int()
        .min(1)
        .max(1_000)
        .default(100)
        .describe('Maximum groups returned per page.'),
    cursor: UtilizationCursorSchema.optional().describe('Opaque continuation cursor from a previous response. Reuse it only with otherwise identical query selectors.'),
});
const ActualUtilizationQuerySchema = UtilizationQueryBaseSchema.extend({
    metrics: z
        .union([z.literal('actualUtilization'), z.array(z.literal('actualUtilization')).min(1).max(3)])
        .transform((value) => [...new Set(Array.isArray(value) ? value : [value])])
        .default(['actualUtilization'])
        .describe('Actual utilization. This measure supports dimensions and a flow filter.'),
    dimensions: z
        .union([UtilizationDimensionSchema, z.array(UtilizationDimensionSchema).max(3)])
        .transform((value) => [...new Set(Array.isArray(value) ? value : [value])])
        .default([])
        .describe('Optional breakdowns, combined within each contract period and rate card. Omit for contract totals.'),
    flowId: z.uuid().optional().describe('Restrict actual utilization to one flow.'),
});
const ContractGrainUtilizationQuerySchema = UtilizationQueryBaseSchema.extend({
    metrics: z
        .union([UtilizationMetricSchema, z.array(UtilizationMetricSchema).min(1).max(3)])
        .transform((value) => [...new Set(Array.isArray(value) ? value : [value])])
        .default(['actualUtilization'])
        .describe('Contract-grain measures. Commitment and bonus credits are not additive across flow, block, or month.'),
    dimensions: z
        .array(UtilizationDimensionSchema)
        .max(0)
        .default([])
        .describe('Contract-grain queries do not accept breakdown dimensions.'),
    flowId: z.never().optional().describe('Contract-grain queries do not accept a flow filter.'),
});
export const UtilizationQuerySchema = z.union([
    ActualUtilizationQuerySchema,
    ContractGrainUtilizationQuerySchema,
]);
const UtilizationRowBaseSchema = z.strictObject({
    contractStart: z.iso.date().nullable(),
    contractEnd: z.iso.date().nullable(),
    rateCard: z.string().max(200).nullable(),
    latestStatement: z
        .string()
        .max(100)
        .nullable()
        .describe('Omni latest-statement label, not ingestion time or a guarantee of source completeness.'),
});
const ActualUtilizationValueSchema = z
    .number()
    .nullable()
    .describe('Charged utilization at the requested grain.');
const CommittedUtilizationValueSchema = z
    .number()
    .nullable()
    .describe('Contracted commitment excluding bonus and builder credits.');
const BonusCreditsValueSchema = z.number().nullable().describe('Contract-level bonus credits.');
const ActualUtilizationRowSchema = UtilizationRowBaseSchema.extend({
    flowId: z.uuid().nullable().optional(),
    flowName: z.string().max(500).nullable().optional(),
    block: z.string().max(200).nullable().optional(),
    month: z.string().max(100).nullable().optional(),
    actualUtilization: ActualUtilizationValueSchema,
    committedUtilization: z.never().optional(),
    bonusCredits: z.never().optional(),
});
const ContractGrainUtilizationRowBaseSchema = UtilizationRowBaseSchema.extend({
    flowId: z.never().optional(),
    flowName: z.never().optional(),
    block: z.never().optional(),
    month: z.never().optional(),
});
const ContractGrainUtilizationRowSchema = z.union([
    ContractGrainUtilizationRowBaseSchema.extend({
        actualUtilization: ActualUtilizationValueSchema,
        committedUtilization: CommittedUtilizationValueSchema.optional(),
        bonusCredits: BonusCreditsValueSchema.optional(),
    }),
    ContractGrainUtilizationRowBaseSchema.extend({
        actualUtilization: ActualUtilizationValueSchema.optional(),
        committedUtilization: CommittedUtilizationValueSchema,
        bonusCredits: BonusCreditsValueSchema.optional(),
    }),
    ContractGrainUtilizationRowBaseSchema.extend({
        actualUtilization: ActualUtilizationValueSchema.optional(),
        committedUtilization: CommittedUtilizationValueSchema.optional(),
        bonusCredits: BonusCreditsValueSchema,
    }),
]);
export const UtilizationRowSchema = z.union([
    ActualUtilizationRowSchema,
    ContractGrainUtilizationRowSchema,
]);
const UtilizationQueryResponseBaseSchema = z.strictObject({
    nextCursor: UtilizationCursorSchema.nullable().describe('Opaque continuation cursor, or null when this is the final page.'),
    scope: z.strictObject({
        organizationId: z.uuid(),
        contractStatus: z.literal('current'),
        contractClassification: z.literal('Platform'),
        timezone: z.literal('America/Los_Angeles'),
    }),
    retrievedAt: z.iso.datetime().describe('Query completion time, not source freshness.'),
});
export const UtilizationQueryResponseSchema = z.union([
    UtilizationQueryResponseBaseSchema.extend({
        rows: z
            .array(ActualUtilizationRowSchema)
            .max(1_000)
            .describe('One bounded page of actual-utilization rows in deterministic server order.'),
    }),
    UtilizationQueryResponseBaseSchema.extend({
        rows: z
            .array(ContractGrainUtilizationRowSchema)
            .max(1_000)
            .describe('One bounded page of contract-grain rows in deterministic server order.'),
    }),
]);
export const UtilizationQueryRoute = {
    method: 'get',
    fullPath: '/analytics/utilization/query',
    summary: 'Query current-contract platform utilization',
    description: 'Query authorized commercial utilization from Omni for current Platform contracts, excluding SOW usage. ' +
        'Use for contract totals and actual-utilization breakdowns by flow, block, statement month, or combinations; ' +
        'provide flowId to narrow analysis to one flow. Commitment and bonus credits are contract-grain measures and must ' +
        'not be summed across breakdowns. Reuse otherwise identical selectors with nextCursor until it is null before ' +
        'treating a result as complete. Pages use deterministic ordering but are independently queried rather than a ' +
        'transactional snapshot, so groups may shift if source data changes between requests. latestStatement is a ' +
        'statement label, not a guarantee of warehouse completeness. Dashboard comparisons require matching filters and ' +
        'grain.',
    tags: ['Analytics'],
    query: UtilizationQuerySchema,
    response: UtilizationQueryResponseSchema,
};
//# sourceMappingURL=utilization.js.map